import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Power, Package, ClipboardList, Plus, Trash2, Pencil, Upload, X, Save, Volume2, VolumeX, Bell, MapPin, Wallet, TrendingUp, Calendar, Truck, Gift, Users, Copy, FileText, MessageCircle, Phone, Trophy, Download, QrCode } from "lucide-react";
import { api, API, imageUrl, whatsappLink, waNumber, downloadFile } from "../lib/api";
import { PromoManager } from "../components/PromoManager";
import { RewardsVendor } from "../components/RewardsVendor";
import { ContractTab } from "../components/ContractVendor";
import { useAuth } from "../context/AuthContext";
import { useNewOrderAlert } from "../lib/useNewOrderAlert";
import { PushToggle } from "../components/PushToggle";
import { VendorAlertsBar } from "../components/VendorAlertsBar";
import { ChangePasswordForm } from "../components/DataSecurityAdmin";
import { VendorOrderCard, STATUS_LABEL } from "../components/VendorOrderCard";
import { useI18n } from "../i18n";

const CATEGORIES = ["Fleurs CBD", "E-liquide", "E-liquide CBD", "Puffs", "Pre-rolls", "Résines", "Chicha", "Chicha CBD", "Accessoires"];
const NOTIFY_MSG = {
  nouvelle: (o) => `Bonjour ${o.customer_name}, on a bien reçu ta commande STUFF #${o.ref} 👍`,
  acceptee: (o) => `Bonjour ${o.customer_name}, ta commande STUFF #${o.ref} est acceptée, on prépare la livraison !`,
  en_livraison: (o) => `Bonjour ${o.customer_name}, ta commande STUFF #${o.ref} est en route 🛵 J'arrive !`,
  livree: (o) => `Bonjour ${o.customer_name}, ta commande STUFF #${o.ref} a été livrée. Merci et à bientôt 🌿${o.invoice_number ? `\n\n🧾 Ta facture ${o.invoice_number} : ${API}/orders/ref/${o.ref}/invoice` : ""}\nSuivi : ${window.location.origin}/suivi/${o.ref}`,
  refusee: (o) => `Bonjour ${o.customer_name}, malheureusement ta commande STUFF #${o.ref} n'a pas pu être traitée. Contacte-moi pour en savoir plus.`,
};
const notifyMessage = (o) => (NOTIFY_MSG[o.status] || NOTIFY_MSG.nouvelle)(o);
const reviewMessage = (o, bonusLabel) =>
  `Bonjour ${o.customer_name}, merci pour ta commande STUFF #${o.ref} 🌿 Ça t'a plu ?\n⭐ Note ta livraison en 1 clic (10 secondes) : ${window.location.origin}/suivi/${o.ref}#avis`
  + (bonusLabel ? `\n🎁 Après ta note, un code ${bonusLabel} t'attend pour ta prochaine commande !` : "")
  + `\nÀ bientôt !`;


export default function VendorDashboard() {
  const { user, refreshMe, setUser } = useAuth();
  const { t } = useI18n();
  const [tab, setTab] = useState("orders");
  const [liveBlock, setLiveBlock] = useState(null);
  const [newContests, setNewContests] = useState([]);
  const [weekState, setWeekState] = useState(null);
  const alert = useNewOrderAlert(!!user && user.role === "vendor");

  useEffect(() => {
    if (!user || user.role !== "vendor") return;
    api.get("/vendor/rewards").then((r) => {
      const seen = JSON.parse(localStorage.getItem("stuff_seen_contests") || "[]");
      setNewContests(r.data.contests.filter((c) => c.status !== "termine" && !seen.includes(c.id)));
      setWeekState({ ...r.data.week_state, tier: r.data.tier });
    }).catch(console.error);
  }, [user]);

  const dismissContest = (id) => {
    const seen = JSON.parse(localStorage.getItem("stuff_seen_contests") || "[]");
    localStorage.setItem("stuff_seen_contests", JSON.stringify([...seen, id]));
    setNewContests((cs) => cs.filter((c) => c.id !== id));
  };

  if (user === null) return <div className="p-10 text-zinc-500">Chargement...</div>;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.role !== "vendor") return <Navigate to="/" replace />;

  const toggleLive = async () => {
    try {
      const { data } = await api.patch("/vendor/live", { is_live: !user.is_live });
      setUser({ ...user, is_live: data.is_live });
      toast.success(data.is_live ? "Tu es EN LIGNE 🔥" : "Tu es hors ligne");
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object") {
        setLiveBlock(d);
        toast.error(d.message);
      } else toast.error(typeof d === "string" ? d : "Erreur");
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-anton text-4xl uppercase text-white">Mon <span className="neon-text-green">espace</span></h1>
          <p className="text-zinc-500 text-sm">{user.name} — {user.city}</p>
        </div>
        <div className="flex flex-col items-stretch sm:items-end gap-1">
          <button
            data-testid="vendor-live-toggle"
            onClick={toggleLive}
            className={`min-h-[44px] flex items-center justify-center gap-2 px-6 py-4 font-anton uppercase text-lg border-2 transition-all ${user.is_live ? "bg-neon-green text-black border-neon-green glow-green" : "bg-transparent text-zinc-400 border-zinc-700"}`}
          >
            <Power className="h-5 w-5" /> {user.is_live ? "En ligne — Go offline" : "Hors ligne — Passer en ligne"}
          </button>
          {user.auto_live && (
            <p data-testid="auto-live-hint" className="text-xs text-neon-green text-center sm:text-right">⚡ Mode auto actif : ta boutique suit tes horaires (onglet Profil)</p>
          )}
        </div>
      </div>

      {user.must_change_password && <div className="mb-6"><ChangePasswordForm forced onDone={refreshMe} /></div>}
      {liveBlock && (
        <div data-testid="live-blocked-banner" className="border border-red-500/60 bg-red-500/10 px-4 py-3 mb-6 text-sm flex flex-col sm:flex-row sm:items-center gap-3">
          <span className="text-red-300"><span className="font-anton uppercase text-red-400">{t("vd.live.blocked")}</span> — {liveBlock.message}</span>
          <div className="flex gap-2 sm:ml-auto">
            <button type="button" data-testid="live-blocked-goto" onClick={() => { setTab(liveBlock.tab); setLiveBlock(null); }} className="min-h-[44px] bg-red-500 text-white text-xs font-bold uppercase px-3">{t("vd.live.goto")}</button>
            <button type="button" data-testid="live-blocked-close" onClick={() => setLiveBlock(null)} className="min-h-[44px] border border-zinc-700 text-zinc-300 text-xs font-bold uppercase px-3">✕</button>
          </div>
        </div>
      )}

      <VendorAlertsBar onGoto={(tabKey) => { if (tabKey === "push") { document.getElementById("push-toggle-block")?.scrollIntoView({ behavior: "smooth" }); return; } setTab(tabKey); }} refreshKey={tab} />

      <div className="mb-6" id="push-toggle-block"><PushToggle /></div>

      {alert.pending.length > 0 && (
        <div data-testid="new-order-alert-banner" className="relative overflow-hidden border-2 border-neon-green bg-neon-green/10 px-4 py-3 mb-6 flex flex-wrap items-center gap-3 shadow-[0_0_25px_rgba(157,255,0,0.35)]">
          <span className="relative flex h-3 w-3 shrink-0"><span className="animate-ping absolute h-full w-full rounded-full bg-neon-green opacity-75" /><span className="relative rounded-full h-3 w-3 bg-neon-green" /></span>
          <Bell className="h-5 w-5 text-neon-green animate-bounce shrink-0" />
          <span className="text-white text-sm">
            <span className="font-anton uppercase text-neon-green text-base">{alert.pending.length} nouvelle{alert.pending.length > 1 ? "s" : ""} commande{alert.pending.length > 1 ? "s" : ""} !</span>
            <span className="text-zinc-300 ml-2">{alert.pending.slice(0, 3).map((o) => `#${o.ref} · ${o.customer_name} · ${Number(o.total || 0).toFixed(2)}€`).join("  |  ")}{alert.pending.length > 3 ? " …" : ""}</span>
          </span>
          <div className="ml-auto flex items-center gap-2">
            <button data-testid="new-order-alert-view" onClick={() => { setTab("orders"); alert.acknowledge(alert.pending.map((o) => o.id)); }} className="text-xs uppercase font-bold bg-neon-green text-black px-4 py-2 hover:bg-white transition-colors">Voir les commandes</button>
            <button data-testid="new-order-alert-sound" onClick={() => { const on = alert.toggleSound(); toast(on ? "Son des commandes activé 🔔" : "Son des commandes coupé"); }} title={alert.soundOn ? "Couper le son" : "Activer le son"} className={`p-2 border transition-colors ${alert.soundOn ? "border-neon-green text-neon-green" : "border-zinc-600 text-zinc-500"}`}>{alert.soundOn ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}</button>
          </div>
        </div>
      )}

      {!user.is_approved && (
        <div data-testid="pending-banner" className="border border-neon-magenta/50 bg-neon-magenta/10 text-neon-magenta px-4 py-3 mb-6 text-sm">
          Ton compte est <span className="font-bold">en attente de validation</span> par l'admin. Tu peux déjà préparer ta boutique et tes produits. Elle sera visible une fois validée.
        </div>
      )}

      {weekState && weekState.next_tier && weekState.up_threshold != null && (
        <div data-testid="tier-progress-bar" className="bg-ink-surface border border-zinc-800 px-4 py-3 mb-6">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-2 text-sm">
            <span className="text-white">
              <span className="font-anton uppercase text-neon-green">Statut {weekState.tier}</span>
              {weekState.current_week_ca >= weekState.up_threshold ? (
                <span className="text-zinc-300"> — Objectif semaine atteint ✅ Continue comme ça pour passer <span className="font-bold text-yellow-400">Vendeur {weekState.next_tier}</span> !</span>
              ) : (
                <span className="text-zinc-300"> — Plus que <span className="font-bold text-neon-green">{(weekState.up_threshold - weekState.current_week_ca).toFixed(2)}€</span> de CA prix usine cette semaine pour viser <span className="font-bold text-yellow-400">Vendeur {weekState.next_tier}</span></span>
              )}
            </span>
            <span data-testid="tier-progress-values" className="text-zinc-500 text-xs">{weekState.current_week_ca.toFixed(2)}€ / {weekState.up_threshold}€</span>
          </div>
          <div className="h-2 bg-zinc-800 overflow-hidden">
            <div className="h-full bg-neon-green transition-[width] duration-700" style={{ width: `${Math.min(100, (weekState.current_week_ca / weekState.up_threshold) * 100)}%` }} />
          </div>
          <p className="text-zinc-600 text-[11px] mt-1.5">{weekState.consec_up > 0 ? `${weekState.consec_up} semaine${weekState.consec_up > 1 ? "s" : ""} consécutive${weekState.consec_up > 1 ? "s" : ""} au-dessus du seuil — ${weekState.promote_consec} pour monter de statut.` : `Passe ${weekState.promote_consec} semaines consécutives au-dessus de ${weekState.up_threshold}€ pour monter de statut.`}</p>
        </div>
      )}

      {weekState && weekState.next_tier && (weekState.consec_up === weekState.promote_consec - 1 || weekState.total_up === weekState.promote_total - 1) && (
        <div data-testid="tier-promo-banner" className="border border-yellow-400/70 bg-yellow-400/10 px-4 py-3 mb-6 text-sm flex flex-wrap items-center gap-3">
          <TrendingUp className="h-5 w-5 text-yellow-400 shrink-0" />
          <span className="text-white">
            <span className="font-anton uppercase text-yellow-400">Plus qu'une semaine !</span> Fais encore <span className="font-bold">{weekState.up_threshold}€</span> de CA prix usine cette semaine et tu passes <span className="font-bold text-yellow-400">Vendeur {weekState.next_tier}</span> 🔥 (semaine en cours : {weekState.current_week_ca.toFixed(2)}€)
          </span>
          <button data-testid="tier-promo-banner-view" onClick={() => setTab("rewards")} className="ml-auto text-xs uppercase font-bold bg-yellow-400 text-black px-3 py-1.5">Ma progression</button>
        </div>
      )}
      {weekState && weekState.keep_threshold != null && weekState.consec_down === weekState.demote_consec - 1 && (
        <div data-testid="tier-demote-banner" className="border border-red-500/70 bg-red-500/10 px-4 py-3 mb-6 text-sm flex flex-wrap items-center gap-3">
          <Bell className="h-5 w-5 text-red-400 shrink-0" />
          <span className="text-white">
            <span className="font-anton uppercase text-red-400">⚠️ Alerte statut :</span> encore 1 semaine sous <span className="font-bold">{weekState.keep_threshold}€</span> de CA prix usine et tu redescends de statut. Fais <span className="font-bold">{weekState.keep_threshold}€</span> cette semaine pour rester <span className="font-bold">Vendeur {weekState.tier}</span> ! (semaine en cours : {weekState.current_week_ca.toFixed(2)}€)
          </span>
          <button data-testid="tier-demote-banner-view" onClick={() => setTab("rewards")} className="ml-auto text-xs uppercase font-bold bg-red-500 text-white px-3 py-1.5">Ma progression</button>
        </div>
      )}

      {newContests.map((c) => (
        <div key={c.id} data-testid="contest-banner" className="border border-neon-green/60 bg-neon-green/10 px-4 py-3 mb-6 text-sm flex flex-wrap items-center gap-3">
          <Trophy className="h-5 w-5 text-neon-green shrink-0" />
          <span className="text-white">
            <span className="font-anton uppercase text-neon-green">{c.status === "a_venir" ? "Jeu concours à venir" : "Nouveau jeu concours"} :</span> {c.name} — {c.metric_label}{c.product_name ? ` (${c.product_name})` : ""}, jusqu'au {c.end_date.split("-").reverse().join("/")}. 1er prix : <span className="font-bold">{c.prizes[0]}</span> 🎁
          </span>
          <div className="ml-auto flex items-center gap-2">
            <button data-testid="contest-banner-view" onClick={() => { dismissContest(c.id); setTab("rewards"); }} className="text-xs uppercase font-bold bg-neon-green text-black px-3 py-1.5">Voir ma place</button>
            <button data-testid="contest-banner-dismiss" onClick={() => dismissContest(c.id)} className="p-1 text-zinc-400 hover:text-white"><X className="h-4 w-4" /></button>
          </div>
        </div>
      ))}

      {!user.contract_signed_at && (
        <div data-testid="contract-warning-banner" className="flex flex-wrap items-center gap-2 border-2 border-red-500 bg-red-500/10 text-red-400 px-4 py-3 mb-5 text-sm font-semibold">
          ⚠️ Contrat non signé — tu dois signer ton contrat de partenariat pour traiter les commandes.
          <button data-testid="contract-warning-goto" onClick={() => setTab("contract")} className="ml-auto bg-red-500 text-white text-xs font-bold uppercase px-3 py-1.5">Signer maintenant</button>
        </div>
      )}
      <div className="flex gap-1 sm:gap-2 border-b border-zinc-800 mb-6 overflow-x-auto no-scrollbar scroll-fade-x sm:[mask-image:none] -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap sm:overflow-visible">
        {[["orders", "Commandes", ClipboardList], ["products", "Stock", Package], ["reassort", "Réassort", Truck], ["earnings", "Gains", Wallet], ["rewards", "Récompenses", Trophy], ["promos", "Promos", Gift], ["contract", "Contrat", FileText], ["profile", "Profil", Pencil]].map(([k, label, Icon]) => (
          <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)} className={`flex items-center gap-2 px-3 sm:px-4 py-3 uppercase text-xs sm:text-sm font-bold transition-colors shrink-0 ${tab === k ? "text-neon-green border-b-2 border-neon-green" : "text-zinc-500 hover:text-white"}`}>
            <Icon className="h-4 w-4" /> {label}
            {k === "contract" && !user.contract_signed_at && <span className="h-2 w-2 rounded-full bg-neon-magenta animate-pulse" />}
          </button>
        ))}
      </div>

      {tab === "orders" && <OrdersTab alert={alert} />}
      {tab === "products" && <StockTab />}
      {tab === "reassort" && <ReassortTab />}
      {tab === "earnings" && <EarningsTab />}
      {tab === "rewards" && <RewardsVendor />}
      {tab === "promos" && <PromoManager scope="vendor" />}
      {tab === "contract" && <ContractTab user={user} onSigned={refreshMe} />}
      {tab === "profile" && <><ProfileTab user={user} onSaved={refreshMe} /><div className="mt-6"><ChangePasswordForm onDone={refreshMe} /></div></>}
    </div>
  );
}

function OrdersTab({ alert }) {
  const { t } = useI18n();
  const { orders, reload: load, soundOn, acknowledge } = alert;
  const toggleSound = () => {
    const next = alert.toggleSound();
    toast(next ? "Son des commandes activé 🔔 (rappel toutes les 20 s tant qu'une commande n'est pas prise en charge)" : "Son des commandes coupé");
  };

  const [giftFor, setGiftFor] = useState(null);
  const [giftChoice, setGiftChoice] = useState("");
  const [giftStock, setGiftStock] = useState(null);
  const [sub, setSub] = useState(null);
  const [query, setQuery] = useState("");
  const [newestFirst, setNewestFirst] = useState(true);
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState(null);
  const [bonusLabel, setBonusLabel] = useState("");
  useEffect(() => { api.get("/settings/review-bonus").then((r) => setBonusLabel(r.data.enabled ? r.data.label : "")).catch(console.error); }, []);
  const reviewMsg = (o) => reviewMessage(o, bonusLabel);
  const markReviewRequested = (id) => api.post(`/vendor/orders/${id}/review-request`).then(load).catch(console.error);
  const askReview = (o) => { window.open(whatsappLink(o.customer_phone, reviewMsg(o)), "_blank", "noopener"); markReviewRequested(o.id); };

  const openGift = async (orderId) => {
    setGiftFor(orderId); setGiftChoice("");
    if (!giftStock) {
      try { const { data } = await api.get("/vendor/stock"); setGiftStock(data.filter((s) => s.quantity > 0)); }
      catch { setGiftStock([]); }
    }
  };

  const addGift = async (orderId) => {
    if (!giftChoice) return;
    try {
      await api.post(`/vendor/orders/${orderId}/gift`, { product_id: giftChoice });
      toast.success("Cadeau ajouté — stock mis à jour 🎁");
      setGiftFor(null); setGiftStock(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur lors de l'ajout du cadeau"); }
  };

  const [codeFor, setCodeFor] = useState(null);
  const [codeVal, setCodeVal] = useState("");
  const [sharing, setSharing] = useState({});
  const watchRef = useRef({});

  const stopShare = useCallback((id, notify = true) => {
    const w = watchRef.current[id];
    if (w?.watchId != null) navigator.geolocation.clearWatch(w.watchId);
    delete watchRef.current[id];
    setSharing((s) => ({ ...s, [id]: false }));
    if (notify) api.post(`/vendor/orders/${id}/location`, { sharing: false }).catch(console.error);
  }, []);

  const startShare = (id) => {
    if (!navigator.geolocation) { toast.error("GPS non disponible sur cet appareil"); return; }
    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const w = watchRef.current[id];
        const now = Date.now();
        if (!w || now - w.last < 10000) return;
        w.last = now;
        api.post(`/vendor/orders/${id}/location`, { lat: pos.coords.latitude, lng: pos.coords.longitude, sharing: true }).catch(console.error);
      },
      () => { toast.error("Impossible d'accéder au GPS — autorise la localisation dans ton navigateur"); stopShare(id, false); },
      { enableHighAccuracy: true, maximumAge: 5000 }
    );
    watchRef.current[id] = { watchId, last: 0 };
    setSharing((s) => ({ ...s, [id]: true }));
    toast.success("Position partagée en live 📍 Garde cette page ouverte pendant la livraison");
  };

  useEffect(() => {
    const refCopy = watchRef;
    return () => { Object.keys(refCopy.current).forEach((id) => {
      const w = refCopy.current[id];
      if (w?.watchId != null) navigator.geolocation.clearWatch(w.watchId);
    }); };
  }, []);

  const arrived = async (id) => {
    try { await api.post(`/vendor/orders/${id}/arrived`); load(); toast.success("Le client est prévenu de ton arrivée 🔔"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const switchPayment = async (o) => {
    const next = (o.payment_method || "").toLowerCase().includes("carte") ? "cash" : "card";
    try { await api.patch(`/vendor/orders/${o.id}/payment-method`, { payment_method: next }); load(); toast.success(next === "card" ? "Paiement : carte ✓" : "Paiement : espèces ✓"); } catch { toast.error("Erreur"); }
  };

  const setStatus = async (id, status, code, reason) => {
    try {
      await api.patch(`/vendor/orders/${id}`, { status, ...(code ? { code } : {}), ...(reason ? { reason } : {}) });
      acknowledge([id]);
      setCodeFor(null); setCodeVal("");
      if (status !== "en_livraison" && sharing[id]) stopShare(id, false);
      // la commande suit son nouveau statut : on bascule sur le bon sous-onglet et on la garde dépliée
      const SUB_OF = { acceptee: "ongoing", en_livraison: "ongoing", livree: "delivered", refusee: "refused" };
      if (SUB_OF[status]) { setSub(SUB_OF[status]); setPage(1); }
      setOpenId(id);
      load();
      if (status === "livree") {
        const o = orders.find((x) => x.id === id);
        toast.success(t("vd.review.toast"), { duration: 12000, action: o ? { label: `⭐ ${t("vd.review.auto")}`, onClick: () => askReview(o) } : undefined });
      } else toast.success(status === "refusee" ? "Commande refusée — stock remis à jour" : "Statut mis à jour");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const invoice = async (o) => {
    try {
      const res = await api.get(`/vendor/orders/${o.id}/invoice`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = o.invoice_number ? `facture-${o.invoice_number}.pdf` : `recu-stuff-${o.ref}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 15000);
      toast.success(o.invoice_number ? "Facture PDF téléchargée 📄" : "Reçu PDF téléchargé 📄");
    } catch { toast.error("Impossible de générer le reçu"); }
  };

  const giftSlot = (o, delivered) => (
    o.gift ? <span data-testid="order-gift" className="text-neon-green font-semibold">Cadeau : {o.gift.name} ✓</span>
    : !delivered && giftFor === o.id ? (
      <span className="inline-flex items-center gap-2 flex-wrap">
        <select data-testid="gift-select" value={giftChoice} onChange={(e) => setGiftChoice(e.target.value)} className="min-h-[44px] bg-ink border border-zinc-700 text-white text-xs px-2 py-1 focus:outline-none focus:border-neon-green">
          <option value="">{giftStock === null ? "Chargement..." : "Choisir un produit..."}</option>
          {(giftStock || []).map((s) => <option key={s.id} value={s.id}>{s.name} (stock: {s.quantity})</option>)}
        </select>
        <button data-testid="gift-confirm" onClick={() => addGift(o.id)} disabled={!giftChoice} className="min-h-[44px] text-xs uppercase font-bold px-3 bg-neon-green text-black disabled:opacity-40">OK</button>
        <button data-testid="gift-cancel" onClick={() => setGiftFor(null)} className="min-h-[44px] text-xs text-zinc-500 uppercase hover:text-white px-2">{t("vd.cancel")}</button>
      </span>
    ) : !delivered ? <button data-testid="add-gift-btn" onClick={() => openGift(o.id)} className="min-h-[44px] text-xs uppercase font-bold px-3 border border-neon-green/60 text-neon-green hover:bg-neon-green hover:text-black transition-colors">+ Ajouter un cadeau</button> : null
  );

  const SUBS = [["new", ["nouvelle", "attente_paiement"]], ["ongoing", ["acceptee", "en_livraison"]], ["delivered", ["livree"]], ["refused", ["refusee", "annulee"]], ["verify", ["a_verifier"]]];
  const counts = Object.fromEntries(SUBS.map(([k, sts]) => [k, orders.filter((o) => sts.includes(o.status)).length]));
  const activeSub = sub || (counts.new > 0 ? "new" : counts.verify > 0 ? "verify" : "ongoing");
  const q = query.trim().toLowerCase().replace(/\s+/g, "");
  const list = orders
    .filter((o) => (SUBS.find(([k]) => k === activeSub)[1]).includes(o.status))
    .filter((o) => !q || (o.ref || "").toLowerCase().includes(q) || (o.customer_name || "").toLowerCase().replace(/\s+/g, "").includes(q) || (o.customer_phone || "").replace(/\s+/g, "").includes(q))
    .sort((x, y) => (newestFirst ? (y.created_at || "").localeCompare(x.created_at || "") : (x.created_at || "").localeCompare(y.created_at || "")));
  const PER = 20;
  const pages = Math.max(1, Math.ceil(list.length / PER));
  const cur = Math.min(page, pages);
  const shown = list.slice((cur - 1) * PER, cur * PER);
  const newCount = counts.new;
  const actions = { setStatus, switchPayment, arrived, startShare, stopShare, invoice, notifyMessage, giftSlot, reviewMessage: reviewMsg, markReviewRequested };
  const codeState = { codeFor, codeVal, setCodeFor, setCodeVal };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-zinc-400 text-sm">
          <Bell className="h-4 w-4 text-neon-green" />
          {newCount > 0 ? <span className="text-neon-green font-bold">{newCount} nouvelle{newCount > 1 ? "s" : ""} commande{newCount > 1 ? "s" : ""}</span> : <span>Aucune nouvelle commande</span>}
          <span className="text-zinc-600 text-xs hidden sm:inline">· actualisation auto</span>
        </div>
        <button data-testid="sound-toggle" onClick={toggleSound} className={`min-h-[44px] flex items-center gap-2 px-3 py-2 border text-xs uppercase font-bold transition-colors ${soundOn ? "border-neon-green text-neon-green" : "border-zinc-700 text-zinc-500"}`}>
          {soundOn ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
          {soundOn ? "Son activé" : "Son coupé"}
        </button>
      </div>

      {/* Sous-onglets avec compteurs */}
      <div className="flex gap-1 overflow-x-auto no-scrollbar -mx-4 px-4 sm:mx-0 sm:px-0 mb-3" data-testid="orders-subtabs">
        {SUBS.map(([k]) => (
          <button key={k} type="button" data-testid={`orders-sub-${k}`} onClick={() => { setSub(k); setPage(1); }} className={`min-h-[44px] shrink-0 px-3 py-2 text-xs uppercase font-bold border-b-2 transition-colors ${activeSub === k ? "border-neon-green text-neon-green" : "border-transparent text-zinc-500 hover:text-white"}`}>
            {t(`vd.tab.${k}`)} <span data-testid={`orders-count-${k}`} className={`ml-1 px-1.5 py-0.5 text-[10px] ${activeSub === k ? "bg-neon-green text-black" : "bg-zinc-800 text-zinc-300"} ${k === "verify" && counts.verify > 0 ? "!bg-orange-400 !text-black" : ""}`}>{counts[k]}</span>
          </button>
        ))}
      </div>
      <div className="flex gap-2 mb-4">
        <input data-testid="orders-search" value={query} onChange={(e) => { setQuery(e.target.value); setPage(1); }} placeholder={t("vd.search")} className="flex-1 min-h-[44px] bg-ink-surface border border-zinc-800 text-white text-sm px-3 focus:outline-none focus:border-neon-green" />
        <button type="button" data-testid="orders-sort" onClick={() => setNewestFirst((v) => !v)} className="min-h-[44px] px-3 border border-zinc-800 text-zinc-300 text-xs uppercase font-bold hover:border-neon-green whitespace-nowrap">{newestFirst ? "↓ " + t("vd.sort.newest") : "↑ " + t("vd.sort.oldest")}</button>
      </div>

      {shown.length === 0 ? (
        <p className="text-zinc-500 py-10 text-center" data-testid="orders-empty">{t("vd.empty")}</p>
      ) : (
        <div className="space-y-3">
          {shown.map((o) => (
            <VendorOrderCard key={o.id} o={o} open={openId === o.id || (o.status === "nouvelle" && openId === null && shown[0]?.id === o.id)} onToggle={() => setOpenId(openId === o.id ? "" : o.id)} actions={actions} sharing={sharing} codeState={codeState} />
          ))}
        </div>
      )}
      {pages > 1 && (
        <div className="flex items-center justify-center gap-3 mt-4" data-testid="orders-pagination">
          <button type="button" data-testid="orders-page-prev" disabled={cur <= 1} onClick={() => setPage(cur - 1)} className="min-h-[44px] px-4 border border-zinc-700 text-zinc-300 text-xs uppercase font-bold disabled:opacity-30">‹ {t("vd.page.prev")}</button>
          <span className="text-zinc-400 text-xs" data-testid="orders-page-info">{cur} {t("vd.page.of")} {pages}</span>
          <button type="button" data-testid="orders-page-next" disabled={cur >= pages} onClick={() => setPage(cur + 1)} className="min-h-[44px] px-4 border border-zinc-700 text-zinc-300 text-xs uppercase font-bold disabled:opacity-30">{t("vd.page.next")} ›</button>
        </div>
      )}
    </div>
  );
}

function StockTab() {
  const [items, setItems] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [activeCat, setActiveCat] = useState(null);

  const load = () => api.get("/vendor/stock").then((r) => {
    setItems(r.data);
    const d = {};
    r.data.forEach((c) => { d[c.id] = { quantity: c.quantity, in_stock: c.in_stock, promo: c.promo, price: c.vendor_price, promo_pct: c.promo_pct || 0 }; });
    setDrafts(d);
  }).catch(console.error);
  useEffect(() => { load(); }, []);

  const categories = useMemo(() => Array.from(new Set(items.map((i) => i.category))), [items]);
  useEffect(() => { if (!activeCat && categories.length) setActiveCat(categories[0]); }, [activeCat, categories]);

  const setDraft = (id, patch) => setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
  const parsePrice = (v) => parseFloat(String(v ?? "").replace(",", "."));

  const save = async (id, override) => {
    const d = { ...drafts[id], ...override };
    const qty = Math.max(0, parseInt(d.quantity) || 0);
    const promo_pct = Math.min(100, Math.max(0, parseFloat(d.promo_pct) || 0));
    try {
      const res = await api.put(`/vendor/stock/${id}`, { quantity: qty, in_stock: !!d.in_stock, promo: !!d.promo, promo_pct });
      setItems((prev) => prev.map((c) => c.id === id ? { ...c, quantity: qty, in_stock: !!d.in_stock, promo: !!d.promo, vendor_price: res.data.price, promo_pct } : c));
      setDraft(id, { quantity: qty, promo_pct });
      toast.success("Boutique mise à jour", { style: { background: "#121212", color: "#9dff00", border: "1px solid #9dff00" } });
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const togglePromo = (id) => {
    const next = !(drafts[id]?.promo);
    setDraft(id, { promo: next });
    save(id, { promo: next });
  };

  const requestRestock = async (p) => {
    if (!window.confirm(`Demander un réassort de "${p.name}" à la maison mère ? (elle fixe la quantité)`)) return;
    try {
      await api.post("/vendor/restock", { items: [{ catalog_id: p.id }] });
      toast.success(`Réassort demandé : ${p.name} 📦`, { style: { background: "#121212", color: "#9dff00", border: "1px solid #9dff00" } });
    } catch { toast.error("Erreur lors de la demande de réassort"); }
  };

  const visible = items.filter((i) => i.category === activeCat);

  return (
    <div>
      <div className="border border-zinc-800 bg-ink-surface p-4 mb-6 text-sm text-zinc-400">
        <Package className="h-4 w-4 text-neon-green inline mr-2" />
        Catalogue et <span className="text-white font-semibold">prix de vente fixés par la maison mère</span>. Gère tes <span className="text-white font-semibold">quantités</span> et applique une <span className="text-neon-magenta font-semibold">promo (%)</span> si besoin.
      </div>

      {categories.length === 0 ? (
        <p className="text-zinc-500 py-10 text-center">Aucun produit au catalogue pour l'instant.</p>
      ) : (
        <>
          <div className="flex gap-2 overflow-x-auto no-scrollbar mb-5 pb-1">
            {categories.map((c) => (
              <button key={c} data-testid={`stock-cat-${c}`} onClick={() => setActiveCat(c)} className={`shrink-0 px-4 py-2 uppercase text-xs font-bold border transition-all ${activeCat === c ? "bg-neon-green text-black border-neon-green" : "bg-transparent text-zinc-400 border-zinc-700 hover:border-neon-green"}`}>{c}</button>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {visible.map((p) => {
              const d = drafts[p.id] || { quantity: p.quantity, in_stock: p.in_stock, promo: p.promo, promo_pct: p.promo_pct || 0 };
              const base = Number(p.price) || 0; // prix imposé par la maison mère
              const pct = Math.min(100, Math.max(0, parseFloat(d.promo_pct) || 0));
              const finalPrice = d.promo && pct > 0 ? base * (1 - pct / 100) : base;
              const dirty = String(d.quantity) !== String(p.quantity) || String(d.promo_pct) !== String(p.promo_pct || 0);
              return (
                <div key={p.id} data-testid="vendor-stock-row" className="bg-ink-surface border border-zinc-800 p-3">
                  <div className="flex items-center gap-3">
                    <img src={imageUrl(p.image_url) || "https://customer-assets.emergentagent.com/job_store-hub-157/artifacts/yyc18991_IMG_4514.jpeg"} alt={p.name} className="h-14 w-14 object-cover" />
                    <div className="flex-1 min-w-0">
                      <h4 className="text-white font-semibold truncate">{p.name}
                        {p.quantity === 0 && <span data-testid="stock-out-badge" className="ml-2 text-[10px] uppercase font-bold text-red-400 border border-red-500/50 px-1.5 py-0.5">Rupture</span>}
                        {p.quantity > 0 && p.quantity <= 3 && <span data-testid="stock-low-badge" className="ml-2 text-[10px] uppercase font-bold text-amber-400 border border-amber-500/50 px-1.5 py-0.5">⚠ Stock bas</span>}
                      </h4>
                      <span className="text-xs text-zinc-500">Prix de vente <span className="text-white font-semibold">{base.toFixed(2)}€</span> <span className="text-zinc-600">(fixé par la maison mère)</span></span>
                    </div>
                    <div className="flex items-center gap-2">
                      <input data-testid="stock-qty-input" type="number" min="0" value={d.quantity} onChange={(e) => setDraft(p.id, { quantity: e.target.value })} className="w-20 bg-ink border border-zinc-700 text-white px-2 py-2 text-center focus:outline-none focus:border-neon-green" title="Quantité en stock" />
                      <button data-testid="stock-save-btn" onClick={() => save(p.id)} disabled={!dirty} className={`p-2 ${dirty ? "text-neon-green hover:bg-ink-hover" : "text-zinc-600"}`} title="Enregistrer"><Save className="h-5 w-5" /></button>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 mt-3">
                    <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer">
                      {p.admin_promo_pct > 0 && <span data-testid="stock-admin-promo" className="mr-2 text-[10px] uppercase font-bold text-black bg-neon-magenta px-2 py-0.5 rounded-full" title="Promotion fixée par STUFF (remise partagée 50/50)">PROMO STUFF -{Math.round(p.admin_promo_pct)}%{p.admin_promo_end ? ` → ${new Date(p.admin_promo_end).toLocaleDateString("fr-FR")}` : ""}</span>}
                      <input type="checkbox" data-testid="stock-promo-toggle" checked={!!d.promo} onChange={() => togglePromo(p.id)} className="accent-[#c026d3]" />
                      <span className={d.promo ? "text-neon-magenta font-bold uppercase" : "uppercase"}>Promo</span>
                    </label>
                    {d.promo && (
                      <label className="flex items-center gap-1 text-xs text-zinc-300">
                        -
                        <input data-testid="stock-promo-pct" type="number" step="1" min="0" max="100" value={d.promo_pct} onChange={(e) => setDraft(p.id, { promo_pct: e.target.value })} className="w-16 bg-ink border border-neon-magenta/50 text-white px-2 py-1.5 text-center focus:outline-none focus:border-neon-magenta" />
                        %
                      </label>
                    )}
                    <span className="ml-auto text-xs">
                      <span className="text-neon-green font-anton text-sm">{finalPrice.toFixed(2)}€</span>
                      {d.promo && pct > 0 && <span className="ml-2 text-zinc-500 line-through">{base.toFixed(2)}€</span>}
                    </span>
                    <button data-testid="stock-restock-btn" onClick={() => requestRestock(p)} className="inline-flex items-center gap-1 uppercase font-bold px-2 py-1 border border-zinc-700 text-zinc-300 hover:border-neon-green hover:text-neon-green transition-colors text-xs"><Truck className="h-3 w-3" /> Réassort</button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function ReassortTab() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [picked, setPicked] = useState({});
  const [history, setHistory] = useState([]);
  const [sugg, setSugg] = useState({ out_of_stock: [], best_sellers: [], sold_4w: {} });

  const load = () => {
    api.get("/vendor/stock").then((r) => setItems(r.data)).catch(console.error);
    api.get("/vendor/restock").then((r) => setHistory(r.data)).catch(console.error);
    // pré-cocher : ruptures + meilleures ventes des 4 dernières semaines
    api.get("/vendor/restock/suggestions").then((r) => {
      setSugg(r.data);
      setPicked((prev) => Object.keys(prev).length ? prev : Object.fromEntries([...r.data.out_of_stock, ...r.data.best_sellers].map((id) => [id, true])));
    }).catch(console.error);
  };
  useEffect(() => { load(); }, []);

  const selected = Object.keys(picked).filter((k) => picked[k]);
  const toggle = (id) => setPicked((q) => ({ ...q, [id]: !q[id] }));
  const allOn = items.length > 0 && items.every((p) => picked[p.id]);
  const checkAll = () => setPicked(allOn ? {} : Object.fromEntries(items.map((p) => [p.id, true])));

  const submit = async () => {
    if (!selected.length) { toast.error("Sélectionne au moins un produit"); return; }
    try {
      await api.post("/vendor/restock", { items: selected.map((catalog_id) => ({ catalog_id })) });
      toast.success("Demande de réassort envoyée à la maison mère", { style: { background: "#121212", color: "#9dff00", border: "1px solid #9dff00" } });
      setPicked({}); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const slip = async (r) => {
    try { await downloadFile(`/restock/${r.id}/slip.pdf`, `BL-${r.ref}.pdf`); toast.success("Bon de livraison téléchargé 📄"); }
    catch { toast.error("Impossible de générer le bon de livraison"); }
  };
  const STATUS = { "demandée": ["Demandé — en attente", "text-yellow-400"], "préparée": ["En préparation", "text-neon-magenta"], "livrée": ["Livré ✓", "text-neon-green"], "annulée": ["Annulée", "text-red-400"] };

  return (
    <div>
      <div className="border border-zinc-800 bg-ink-surface p-4 mb-6 text-sm text-zinc-400">
        <Truck className="h-4 w-4 text-neon-green inline mr-2" />
        Coche les <span className="text-white font-semibold">produits à réapprovisionner</span>. La maison mère fixe les quantités, prépare le bon de livraison et te livre.
        <p className="text-xs text-zinc-500 mt-1" data-testid="reassort-suggested-hint">✨ {t("vd.restock.suggested")}</p>
      </div>
      <div className="flex justify-end mb-3">
        <button type="button" data-testid="reassort-check-all" onClick={checkAll} className="min-h-[44px] px-4 border border-zinc-700 text-zinc-300 text-xs uppercase font-bold hover:border-neon-green hover:text-neon-green">{allOn ? t("vd.restock.uncheckAll") : t("vd.restock.checkAll")}</button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        {items.map((p) => {
          const on = !!picked[p.id];
          const isOut = sugg.out_of_stock.includes(p.id) || p.quantity <= 0;
          const isTop = sugg.best_sellers.includes(p.id);
          return (
            <button key={p.id} type="button" data-testid="reassort-row" aria-pressed={on} onClick={() => toggle(p.id)}
              className={`min-h-[44px] flex items-center gap-3 border p-3 text-left transition-colors ${on ? "bg-neon-green/10 border-neon-green" : "bg-ink-surface border-zinc-800 hover:border-zinc-600"}`}>
              <img src={imageUrl(p.image_url) || "https://customer-assets.emergentagent.com/job_store-hub-157/artifacts/yyc18991_IMG_4514.jpeg"} alt={p.name} className="h-12 w-12 object-cover" />
              <div className="flex-1 min-w-0">
                <h4 className="text-white font-semibold truncate text-sm">{p.name}</h4>
                <span className="text-xs text-zinc-500">{p.category} · en stock : <span className={p.quantity <= 3 ? "text-yellow-400 font-bold" : ""}>{p.quantity}</span></span>
                <span className="block mt-0.5 flex gap-1 flex-wrap">
                  {isOut && <span data-testid="reassort-out-badge" className="text-[10px] uppercase font-bold text-red-400 border border-red-500/50 px-1.5">{t("vd.restock.out")}</span>}
                  {isTop && <span data-testid="reassort-top-badge" className="text-[10px] uppercase font-bold text-neon-magenta border border-neon-magenta/50 px-1.5">🔥 {t("vd.restock.top")} ×{sugg.sold_4w[p.id] || 0}</span>}
                </span>
              </div>
              <span data-testid="reassort-check" className={`h-6 w-6 shrink-0 border-2 flex items-center justify-center ${on ? "bg-neon-green border-neon-green text-black" : "border-zinc-600"}`}>{on && "✓"}</span>
            </button>
          );
        })}
      </div>
      <button data-testid="reassort-submit" onClick={submit} disabled={selected.length === 0} className="w-full bg-neon-green text-black font-anton uppercase py-3 glow-green disabled:opacity-40 mb-8">Demander le réassort ({selected.length} produit{selected.length > 1 ? "s" : ""})</button>

      <h3 className="font-anton text-lg uppercase text-white mb-3">Mes demandes</h3>
      {history.length === 0 ? <p className="text-zinc-500">Aucune demande pour l'instant.</p> : (
        <div className="space-y-2">
          {history.map((r) => (
            <div key={r.id} data-testid="reassort-history-row" className="bg-ink-surface border border-zinc-800 p-3">
              <div className="flex flex-wrap justify-between items-center gap-2">
                <span className="font-mono text-neon-green">#{r.ref}</span>
                <span className={`text-xs uppercase font-bold ${(STATUS[r.status] || ["", "text-zinc-400"])[1]}`}>{(STATUS[r.status] || [r.status])[0]}</span>
                {!["demandée", "annulée"].includes(r.status) && (
                  <button data-testid="reassort-slip-btn" onClick={() => slip(r)} className="inline-flex items-center gap-1 text-[11px] uppercase font-bold border border-zinc-700 text-zinc-300 px-2 py-1 hover:border-neon-green hover:text-neon-green transition-colors"><FileText className="h-3.5 w-3.5" /> Bon de livraison</button>
                )}
              </div>
              {r.vendor_notice && <p data-testid="reassort-notice" className="text-orange-300 text-xs mt-1 border border-orange-400/40 bg-orange-400/5 px-2 py-1">⚠️ {t("vd.restock.removed")} : {(r.removed_items || []).map((i) => i.name).join(", ")}{r.status === "annulée" ? " — demande annulée" : ""}</p>}
              <div className="text-zinc-400 text-xs mt-1">
                {r.items.map((i) => (
                  <span key={i.catalog_id} className="mr-3">{i.name}{i.quantity > 0 ? <span className="text-neon-green font-bold"> x{i.quantity}</span> : <span className="text-zinc-600"> (qté fixée par la maison mère)</span>}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EarningsTab() {
  const [data, setData] = useState(null);
  const [openWeek, setOpenWeek] = useState(null);
  const [ref, setRef] = useState(null);
  const [tip, setTip] = useState(null);
  const { t } = useI18n();
  useEffect(() => { api.get("/vendor/earnings").then((r) => { setData(r.data); setOpenWeek(r.data.current_week.week_start); }).catch(console.error); }, []);
  useEffect(() => { api.get("/vendor/referral").then((r) => setRef(r.data)).catch(console.error); }, []);

  if (!data) return <p className="text-zinc-500 py-10 text-center">Chargement...</p>;
  const cw = data.current_week;
  const shareLink = ref ? `${window.location.origin}/register?ref=${ref.code}` : "";
  const copy = (txt) => { navigator.clipboard?.writeText(txt); toast.success("Copié !", { style: { background: "#121212", color: "#9dff00", border: "1px solid #9dff00" } }); };
  const exportCsv = async () => {
    try {
      const res = await api.get("/vendor/earnings/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `mes-gains-stuff-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 15000);
      toast.success("Export CSV téléchargé 📊");
    } catch { toast.error("Erreur lors de l'export"); }
  };

  return (
    <div>
      {/* Parrainage / referral */}
      {ref && (
        <div data-testid="referral-block" className="bg-ink-surface border border-neon-green/40 p-5 mb-6 glow-green">
          <div className="flex items-center gap-2 mb-3">
            <Gift className="h-5 w-5 text-neon-green" />
            <h3 className="font-anton text-lg uppercase text-white">Parrainage — gagne {ref.rate_pct}% à vie</h3>
          </div>
          <p className="text-zinc-400 text-xs mb-3">Partage ton code : pour chaque vendeur qui s'inscrit avec, tu touches <span className="text-neon-green font-bold">{ref.rate_pct}% de ses gains</span> chaque semaine (offert par la maison mère, ça ne réduit rien pour lui).</p>
          {ref.next_tier && (
            <p data-testid="referral-next-tier" className="text-neon-magenta text-xs mb-3 font-semibold">🎯 Plus que {ref.next_tier.needed} filleul{ref.next_tier.needed > 1 ? "s" : ""} pour passer à {ref.next_tier.rate_pct}% !</p>
          )}
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="font-anton text-2xl text-neon-green tracking-wider" data-testid="referral-code">{ref.code}</span>
            <button data-testid="referral-copy-code" onClick={() => copy(ref.code)} className="flex items-center gap-1 text-xs text-zinc-300 border border-zinc-700 px-2 py-1 hover:border-neon-green"><Copy className="h-3 w-3" /> Copier le code</button>
            <button data-testid="referral-copy-link" onClick={() => copy(shareLink)} className="flex items-center gap-1 text-xs text-zinc-300 border border-zinc-700 px-2 py-1 hover:border-neon-magenta"><Copy className="h-3 w-3" /> Copier le lien d'invitation</button>
          </div>
          <div className="flex flex-wrap gap-4 text-sm">
            <span className="flex items-center gap-1 text-zinc-300"><Users className="h-4 w-4 text-neon-magenta" /> <span data-testid="referral-count" className="font-bold text-white">{ref.referred_count}</span> filleul{ref.referred_count > 1 ? "s" : ""}</span>
            <span className="text-zinc-300">Cette semaine : <span className="text-neon-green font-bold" data-testid="referral-current">{(cw.referral_realized || 0).toFixed(2)}€</span>{(cw.referral_pending || 0) > 0 && <span className="text-zinc-500"> (+{cw.referral_pending.toFixed(2)}€ en cours)</span>}</span>
            <span className="text-zinc-300">Total parrainage perçu : <span className="text-neon-green font-bold" data-testid="referral-total">{(data.total_referral || 0).toFixed(2)}€</span></span>
          </div>
        </div>
      )}

      {/* 3 indicateurs : déjà touché / en attente / à reverser à STUFF */}
      {data.summary && (
        <div data-testid="earnings-summary" className="bg-ink-surface border border-neon-magenta/40 p-4 sm:p-5 mb-6">
          <h3 className="font-anton text-lg uppercase text-white mb-3 flex items-center flex-wrap gap-2">Mes gains <span className="text-zinc-500 text-xs normal-case font-sans">Statut {data.summary.tier}</span>
            <button data-testid="vendor-earnings-export-csv" onClick={exportCsv} className="ml-auto min-h-[44px] flex items-center gap-1.5 text-xs uppercase font-bold border border-neon-green text-neon-green px-3 hover:bg-neon-green hover:text-black transition-colors font-sans"><Download className="h-3.5 w-3.5" /> Export CSV</button>
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              ["received", data.summary.deja_touche, "text-neon-green", `${t("vd.earn.gainProducts")} ${data.summary.deja_gain.toFixed(2)}€ + ${t("vd.earn.deliveryHt")} ${data.summary.deja_livraison_ht.toFixed(2)}€`],
              ["pending", data.summary.en_attente, "text-white", null],
              ["due", data.summary.a_reverser, "text-neon-magenta", `${t("vd.earn.week")} · usine ${data.summary.a_reverser_detail.prix_usine.toFixed(2)}€ + TVA ${data.summary.a_reverser_detail.tva.toFixed(2)}€${data.summary.a_reverser_detail.club_ht ? ` + Club ${data.summary.a_reverser_detail.club_ht.toFixed(2)}€` : ""}`],
            ].map(([k, val, color, sub]) => (
              <div key={k} data-testid={`earnings-kpi-${k}`} className="border border-zinc-800 p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="text-zinc-400 text-[11px] uppercase font-bold">{t(`vd.earn.${k}`)}</div>
                  <button type="button" data-testid={`earnings-kpi-${k}-info`} aria-label="info" onClick={() => setTip(tip === k ? null : k)} className={`min-h-[32px] min-w-[32px] -mt-1 -mr-1 flex items-center justify-center text-xs border rounded-full ${tip === k ? "border-neon-green text-neon-green" : "border-zinc-700 text-zinc-500"}`}>?</button>
                </div>
                <div className={`font-anton text-2xl sm:text-3xl ${color}`}>{Number(val).toFixed(2)}€</div>
                {sub && <div className="text-zinc-600 text-[11px] mt-0.5">{sub}</div>}
                {k === "due" && data.summary.factures_non_reglees > 0 && <div data-testid="earnings-unpaid-invoices" className="text-orange-300 text-[11px] mt-0.5">+ {data.summary.factures_non_reglees.toFixed(2)}€ {t("vd.earn.unpaid")} ({data.summary.factures_non_reglees_count})</div>}
                {tip === k && <p data-testid={`earnings-kpi-${k}-tip`} className="text-zinc-300 text-xs mt-2 border-t border-zinc-800 pt-2">{t(`vd.earn.${k}.tip`)}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Current week summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <div className="bg-ink-surface border border-neon-green/40 p-5 glow-green">
          <Wallet className="h-5 w-5 text-neon-green mb-2" />
          <div className="font-anton text-3xl text-neon-green" data-testid="earnings-current-realized">{cw.realized.toFixed(2)}€</div>
          <div className="text-zinc-400 text-xs uppercase">Gain produits cette semaine (livré)</div>
          {(cw.referral_realized || 0) > 0 && <div className="text-neon-green/70 text-xs mt-1">dont +{cw.referral_realized.toFixed(2)}€ parrainage</div>}
        </div>
        <div className="bg-ink-surface border border-zinc-800 p-5">
          <TrendingUp className="h-5 w-5 text-neon-magenta mb-2" />
          <div className="font-anton text-3xl text-white" data-testid="earnings-current-pending">{cw.pending.toFixed(2)}€</div>
          <div className="text-zinc-400 text-xs uppercase">Gain produits en cours (non livré)</div>
        </div>
        <div className="bg-ink-surface border border-zinc-800 p-5">
          <Calendar className="h-5 w-5 text-zinc-400 mb-2" />
          <div className="font-anton text-3xl text-white">{cw.orders_count}</div>
          <div className="text-zinc-400 text-xs uppercase">Commandes cette semaine</div>
        </div>
      </div>
      <div className="flex items-center gap-2 text-zinc-500 text-xs mb-4">
        <Calendar className="h-4 w-4" /> Semaine en cours : <span className="text-zinc-300 font-semibold">{cw.label}</span>
      </div>
      <p className="text-zinc-600 text-xs mb-6">La semaine commence le lundi à 09h00 et se termine le lundi suivant à 08h55. Les montants ci-dessous sont les <span className="text-zinc-400">gains produits</span> (hors livraison).</p>

      {/* Weekly history */}
      <h3 className="font-anton text-lg uppercase text-white mb-3">Historique par semaine</h3>
      {data.weeks.length === 0 ? <p className="text-zinc-500">Aucun gain pour l'instant.</p> : (
        <div className="space-y-3">
          {data.weeks.map((w) => (
            <div key={w.week_start} data-testid="earnings-week-row" className="bg-ink-surface border border-zinc-800">
              <button onClick={() => setOpenWeek(openWeek === w.week_start ? null : w.week_start)} className="w-full flex items-center justify-between p-4 text-left">
                <div>
                  <div className="text-white font-semibold text-sm">{w.label}</div>
                  <div className="text-zinc-500 text-xs">{w.orders_count} commande{w.orders_count > 1 ? "s" : ""}{(w.referral_realized || 0) > 0 ? ` · +${w.referral_realized.toFixed(2)}€ parrainage` : ""}</div>
                </div>
                <div className="text-right">
                  <div className="font-anton text-xl text-neon-green">{w.realized.toFixed(2)}€</div>
                  {w.pending > 0 && <div className="text-zinc-500 text-xs">+ {w.pending.toFixed(2)}€ en cours</div>}
                </div>
              </button>
              {openWeek === w.week_start && (
                <div className="border-t border-zinc-800 px-4 py-2">
                  {(w.referral_realized || 0) > 0 || (w.referral_pending || 0) > 0 ? (
                    <div className="flex items-center justify-between py-1.5 text-sm border-b border-zinc-900">
                      <span className="text-neon-green flex items-center gap-1"><Gift className="h-3 w-3" /> Bonus parrainage (5%)</span>
                      <span className="text-neon-green font-semibold">{(w.referral_realized || 0).toFixed(2)}€{(w.referral_pending || 0) > 0 ? ` (+${w.referral_pending.toFixed(2)}€ en cours)` : ""}</span>
                    </div>
                  ) : null}
                  {w.orders.map((o, idx) => (
                    <div key={o.ref || idx} className="flex items-center justify-between py-1.5 text-sm border-b border-zinc-900 last:border-0">
                      <span className="text-zinc-400">#{o.ref} · {o.customer_name}</span>
                      <span className="flex items-center gap-2">
                        <span className={`text-xs uppercase ${o.status === "livree" ? "text-neon-green" : o.status === "refusee" ? "text-red-400" : "text-zinc-500"}`}>{o.status}</span>
                        <span className="text-white font-semibold">{o.earnings.toFixed(2)}€</span>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <VendorInvoicesSection />
    </div>
  );
}

function VendorInvoicesSection() {
  const [invoices, setInvoices] = useState([]);
  useEffect(() => { api.get("/vendor/invoices").then((r) => setInvoices(r.data)).catch(console.error); }, []);
  const download = async (inv) => {
    try {
      const res = await api.get(`/vendor-invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${inv.number}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 15000);
      toast.success("Facture PDF téléchargée 🧾");
    } catch { toast.error("Impossible de générer la facture"); }
  };
  return (
    <div className="mt-8">
      <h3 className="font-anton text-lg uppercase text-white mb-1 flex items-center gap-2"><FileText className="h-5 w-5 text-neon-magenta" /> Mes factures hebdomadaires</h3>
      <p className="text-zinc-500 text-xs mb-4">Chaque lundi, une facture est générée automatiquement pour la semaine écoulée : elle indique le montant (prix usine + TVA) à reverser à STUFF CBD et ce que tu conserves.</p>
      {invoices.length === 0 ? <p className="text-zinc-600 text-sm">Aucune facture pour l'instant (générées après la première semaine complète de ventes livrées).</p> : (
        <div className="space-y-2">
          {invoices.map((inv) => (
            <div key={inv.id} data-testid="vendor-invoice-row" className="bg-ink-surface border border-zinc-800 p-4 flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-[180px]">
                <div className="text-white font-semibold text-sm flex items-center gap-2">{inv.number}
                  {inv.paid ? (
                    <span data-testid="vendor-invoice-paid" className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-neon-green border border-neon-green/60 px-1.5 py-0.5">✓ Payé</span>
                  ) : (
                    <span data-testid="vendor-invoice-pending" className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-amber-400 border border-amber-400/60 px-1.5 py-0.5">En attente</span>
                  )}
                </div>
                <div className="text-zinc-500 text-xs">{inv.label}</div>
              </div>
              <div className="text-right">
                <div className="text-neon-magenta font-anton text-lg">{inv.totals.total_due.toFixed(2)}€</div>
                <div className="text-zinc-500 text-[11px] uppercase">À reverser</div>
              </div>
              <div className="text-right">
                <div className="text-neon-green font-anton text-lg">{inv.totals.total_kept.toFixed(2)}€</div>
                <div className="text-zinc-500 text-[11px] uppercase">Tu conserves</div>
              </div>
              <button data-testid="vendor-invoice-pdf-btn" onClick={() => download(inv)} className="flex items-center gap-1.5 text-xs uppercase font-bold border border-neon-magenta text-neon-magenta px-3 py-2 hover:bg-neon-magenta hover:text-white transition-colors"><Download className="h-3.5 w-3.5" /> PDF</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function QrStats() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/vendor/qr-stats").then((r) => setStats(r.data)).catch(console.error); }, []);
  if (!stats) return null;
  return (
    <p data-testid="qr-stats" className="text-neon-green text-xs mt-1 font-semibold">
      📱 {stats.total} scan{stats.total > 1 ? "s" : ""} au total · {stats.last7} ces 7 derniers jours
    </p>
  );
}

function ProfileTab({ user, onSaved }) {
  const [form, setForm] = useState({
    name: user.name || "", whatsapp: user.whatsapp || "", telegram: user.telegram || "", snapchat: user.snapchat || "",
    phone: user.phone || "", siret: user.siret || "", address: user.address || "", description: user.description || "", delivery_time: user.delivery_time || "< 2h", opening_hours: user.opening_hours || "", avatar_url: user.avatar_url || "",
    schedule: user.schedule || {}, auto_live: !!user.auto_live, vacation_from: user.vacation_from || "", vacation_to: user.vacation_to || "",
    radius_km: user.radius_km ?? 10, delivery_base: user.delivery_base ?? 3.5, delivery_per_km: user.delivery_per_km ?? 0.8,
    free_threshold: user.free_threshold ?? 69, lat: user.lat ?? null, lng: user.lng ?? null, accepts_card: !!user.accepts_card,
  });
  const [uploading, setUploading] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updDay = (k, patch) => setForm((f) => ({ ...f, schedule: { ...f.schedule, [k]: { ...(f.schedule[k] || {}), ...patch } } }));
  const DAYS = [["mon", "Lundi"], ["tue", "Mardi"], ["wed", "Mercredi"], ["thu", "Jeudi"], ["fri", "Vendredi"], ["sat", "Samedi"], ["sun", "Dimanche"]];

  const uploadAvatar = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/vendor/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      upd("avatar_url", data.url);
    } catch { toast.error("Échec upload"); }
    finally { setUploading(false); }
  };

  const setPosition = () => {
    if (!navigator.geolocation) { toast.error("Géolocalisation indisponible"); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => { setForm((f) => ({ ...f, lat: pos.coords.latitude, lng: pos.coords.longitude })); toast.success("Position enregistrée"); },
      () => toast.error("Localisation refusée"),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      radius_km: parseFloat(form.radius_km) || 10,
      delivery_base: parseFloat(form.delivery_base) || 0,
      delivery_per_km: parseFloat(form.delivery_per_km) || 0,
      free_threshold: parseFloat(form.free_threshold) || 0,
    };
    try { await api.put("/vendor/profile", payload); await onSaved(); toast.success("Profil enregistré"); }
    catch { toast.error("Erreur"); }
  };

  return (
    <form onSubmit={save} className="bg-ink-surface border border-zinc-800 p-6 space-y-4 max-w-lg" data-testid="profile-form">
      <div className="flex items-center gap-4">
        <label className="h-20 w-20 rounded-full border-2 border-neon-green overflow-hidden cursor-pointer flex items-center justify-center bg-ink" data-testid="avatar-label">
          {form.avatar_url ? <img src={imageUrl(form.avatar_url)} alt="" className="h-full w-full object-cover" /> : <Upload className="h-6 w-6 text-zinc-500" />}
          <input type="file" accept="image/*" className="hidden" data-testid="avatar-input" onChange={(e) => uploadAvatar(e.target.files[0])} />
        </label>
        <p className="text-zinc-500 text-sm">Logo / photo de ta boutique</p>
      </div>
      <input data-testid="profile-name" value={form.name} onChange={(e) => upd("name", e.target.value)} placeholder="Nom de la boutique" className="w-full bg-ink border border-zinc-700 text-white px-3 py-2 focus:outline-none focus:border-neon-green" />
      <textarea data-testid="profile-desc" value={form.description} onChange={(e) => upd("description", e.target.value)} rows={2} placeholder="Description de ta boutique" className="w-full bg-ink border border-zinc-700 text-white px-3 py-2 focus:outline-none focus:border-neon-green" />
      <input data-testid="profile-phone" value={form.phone} onChange={(e) => upd("phone", e.target.value)} placeholder="Téléphone" className="w-full bg-ink border border-zinc-700 text-white px-3 py-2 focus:outline-none focus:border-neon-green" />
      <input data-testid="profile-siret" value={form.siret} onChange={(e) => upd("siret", e.target.value.replace(/\D/g, "").slice(0, 14))} placeholder="SIRET (14 chiffres) — requis pour signer le contrat" className="w-full bg-ink border border-zinc-700 text-white px-3 py-2 focus:outline-none focus:border-neon-green" />
      <input data-testid="profile-address" value={form.address} onChange={(e) => upd("address", e.target.value)} placeholder="Adresse complète (n°, rue, CP, ville) — requise pour le contrat" className="w-full bg-ink border border-zinc-700 text-white px-3 py-2 focus:outline-none focus:border-neon-green" />
      <input data-testid="profile-whatsapp" value={form.whatsapp} onChange={(e) => upd("whatsapp", e.target.value)} placeholder="WhatsApp (ex: 33612345678)" className="w-full bg-ink border border-zinc-700 text-white px-3 py-2 focus:outline-none focus:border-neon-green" />
      <input data-testid="profile-snapchat" value={form.snapchat} onChange={(e) => upd("snapchat", e.target.value)} placeholder="Snapchat (pseudo, ex: stuffcbd75)" className="w-full bg-ink border border-zinc-700 text-white px-3 py-2 focus:outline-none focus:border-neon-green" />

      <div className="border border-zinc-800 bg-ink p-4 flex flex-wrap items-center gap-3">
        <QrCode className="h-8 w-8 text-neon-green shrink-0" />
        <div className="flex-1 min-w-[200px]">
          <p className="text-white font-semibold text-sm">Mon QR code boutique</p>
          <p className="text-zinc-500 text-xs">Carte imprimable pour tes flyers et cartes de visite — scan direct vers ta boutique.</p>
          <QrStats />
        </div>
        <button type="button" data-testid="download-qr-btn" onClick={async () => {
          try {
            const res = await api.get(`/vendors/${user.id}/qr-card`, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([res.data], { type: "image/png" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `qr-boutique-${(user.city || "stuff").toLowerCase()}.png`;
            a.click();
            setTimeout(() => URL.revokeObjectURL(url), 15000);
            toast.success("QR code téléchargé 📱");
          } catch { toast.error("Impossible de générer le QR code"); }
        }} className="flex items-center gap-1.5 text-xs uppercase font-bold border border-neon-green text-neon-green px-3 py-2 hover:bg-neon-green hover:text-black transition-colors"><Download className="h-3.5 w-3.5" /> Télécharger</button>
      </div>
      <input data-testid="profile-delivery" value={form.delivery_time} onChange={(e) => upd("delivery_time", e.target.value)} placeholder="Délai de livraison (ex: ~30 min)" className="w-full bg-ink border border-zinc-700 text-white px-3 py-2 focus:outline-none focus:border-neon-green" />

      <div className="border-t border-zinc-800 pt-4">
        <h3 className="font-anton text-lg uppercase text-white mb-1">Horaires de travail</h3>
        <p className="text-zinc-500 text-xs mb-3">Affichés aux clients (« Aujourd'hui : 08:00 – 23:00 »). Coche « Repos » pour tes jours off.</p>
        <div className="space-y-2" data-testid="profile-schedule">
          {DAYS.map(([k, label]) => {
            const d = form.schedule[k] || {};
            return (
              <div key={k} className="flex items-center gap-2 flex-wrap">
                <span className="w-20 text-zinc-400 text-xs uppercase shrink-0">{label}</span>
                <input data-testid={`schedule-${k}-open`} type="time" value={d.open || ""} disabled={!!d.off} onChange={(e) => updDay(k, { open: e.target.value })} className="bg-ink border border-zinc-700 text-white px-2 py-1.5 text-sm focus:outline-none focus:border-neon-green disabled:opacity-30" />
                <span className="text-zinc-600">–</span>
                <input data-testid={`schedule-${k}-close`} type="time" value={d.close || ""} disabled={!!d.off} onChange={(e) => updDay(k, { close: e.target.value })} className="bg-ink border border-zinc-700 text-white px-2 py-1.5 text-sm focus:outline-none focus:border-neon-green disabled:opacity-30" />
                <label className="flex items-center gap-1.5 text-zinc-400 text-xs ml-1">
                  <input data-testid={`schedule-${k}-off`} type="checkbox" checked={!!d.off} onChange={(e) => updDay(k, { off: e.target.checked })} className="accent-[#9dff00]" /> Repos
                </label>
              </div>
            );
          })}
        </div>
        <label className="flex items-start gap-2 mt-4 text-sm text-zinc-300 cursor-pointer">
          <input data-testid="profile-auto-live" type="checkbox" checked={form.auto_live} onChange={(e) => upd("auto_live", e.target.checked)} className="accent-[#9dff00] mt-0.5" />
          <span>⚡ <span className="font-bold text-white">Passage en ligne automatique</span> selon mes horaires — ta boutique s'ouvre et se ferme toute seule (le bouton manuel est ignoré tant que ce mode est actif).</span>
        </label>
      </div>

      <div className="border-t border-zinc-800 pt-4">
        <h3 className="font-anton text-lg uppercase text-white mb-1">💳 Paiement à la livraison</h3>
        <p className="text-zinc-500 text-xs mb-3">Les espèces sont toujours acceptées. Active la carte bancaire si tu encaisses avec Tap to Pay sur ton téléphone (app bancaire ou SumUp/Stripe Tap to Pay) : le client pourra choisir « CB » au checkout.</p>
        <label className="flex items-start gap-2 text-sm text-zinc-300 cursor-pointer">
          <input data-testid="profile-accepts-card" type="checkbox" checked={form.accepts_card} onChange={(e) => upd("accepts_card", e.target.checked)} className="accent-[#9dff00] mt-0.5" />
          <span>💳 <span className="font-bold text-white">J'accepte la carte bancaire (Tap to Pay)</span> — encaissement sur mon téléphone à la remise en main propre.</span>
        </label>
      </div>

      <div className="border-t border-zinc-800 pt-4">
        <h3 className="font-anton text-lg uppercase text-white mb-1">🏖️ Mode vacances</h3>
        <p className="text-zinc-500 text-xs mb-3">Planifie tes vacances : pendant cette période ta boutique apparaît « En vacances » et les clients peuvent précommander pour ton retour. Laisse vide pour désactiver.</p>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-zinc-400 text-xs uppercase">Du
            <input data-testid="profile-vacation-from" type="date" value={form.vacation_from} onChange={(e) => upd("vacation_from", e.target.value)} className="mt-1 w-full bg-ink border border-zinc-700 text-white px-3 py-2 focus:outline-none focus:border-neon-green" />
          </label>
          <label className="text-zinc-400 text-xs uppercase">Au (inclus)
            <input data-testid="profile-vacation-to" type="date" value={form.vacation_to} onChange={(e) => upd("vacation_to", e.target.value)} className="mt-1 w-full bg-ink border border-zinc-700 text-white px-3 py-2 focus:outline-none focus:border-neon-green" />
          </label>
        </div>
        {form.vacation_from && form.vacation_to && (
          <button type="button" data-testid="profile-vacation-clear" onClick={() => setForm((f) => ({ ...f, vacation_from: "", vacation_to: "" }))} className="mt-2 text-xs uppercase font-bold text-zinc-400 underline hover:text-neon-green">Annuler les vacances</button>
        )}
      </div>

      <div className="border-t border-zinc-800 pt-4">
        <h3 className="font-anton text-lg uppercase text-white mb-1">Livraison & zone</h3>
        <p className="text-zinc-500 text-xs mb-3">Frais = base (les 2 premiers km inclus) + prix/km au-delà de 2 km. Gratuit au-delà du seuil. Grille recommandée : 3,50€ + 0,80€/km.</p>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-zinc-400 text-xs uppercase">Rayon (km)
            <input data-testid="profile-radius" type="number" step="0.5" value={form.radius_km} onChange={(e) => upd("radius_km", e.target.value)} className="mt-1 w-full bg-ink border border-zinc-700 text-white px-3 py-2 focus:outline-none focus:border-neon-green" />
          </label>
          <label className="text-zinc-400 text-xs uppercase">Frais de base (€)
            <input data-testid="profile-base" type="number" step="0.5" value={form.delivery_base} onChange={(e) => upd("delivery_base", e.target.value)} className="mt-1 w-full bg-ink border border-zinc-700 text-white px-3 py-2 focus:outline-none focus:border-neon-green" />
          </label>
          <label className="text-zinc-400 text-xs uppercase">Prix / km (€)
            <input data-testid="profile-perkm" type="number" step="0.1" value={form.delivery_per_km} onChange={(e) => upd("delivery_per_km", e.target.value)} className="mt-1 w-full bg-ink border border-zinc-700 text-white px-3 py-2 focus:outline-none focus:border-neon-green" />
          </label>
          <div className="text-zinc-400 text-xs uppercase">Gratuit dès (€)
            <div data-testid="profile-free" className="mt-1 w-full bg-ink/60 border border-zinc-800 text-neon-green font-anton px-3 py-2 flex items-center justify-between">69 € <span className="text-[10px] text-zinc-500 font-outfit normal-case">règle STUFF, identique pour tous</span></div>
          </div>
        </div>
        <button type="button" data-testid="profile-set-position" onClick={setPosition} className="mt-3 flex items-center gap-2 border border-neon-magenta text-neon-magenta uppercase text-xs font-bold px-3 py-2 hover:bg-neon-magenta hover:text-white transition-colors">
          <MapPin className="h-4 w-4" /> {form.lat ? "Position définie ✓ (mettre à jour)" : "Définir ma position de départ"}
        </button>
      </div>

      <button type="submit" data-testid="profile-save" disabled={uploading} className="flex items-center gap-2 bg-neon-green text-black font-anton uppercase px-5 py-2 glow-green disabled:opacity-50"><Save className="h-4 w-4" /> Enregistrer</button>
    </form>
  );
}
